import React, { useEffect, useRef } from 'react';

import { IBerryRecommendedItem } from 'src/models/berry.model';
import { IChatMessage } from '../types';
import useVisibleViewport from '../hooks/useVisibleViewport';
import CartCompleteCard from './CartCompleteCard';
import ChatInput from './ChatInput';
import ChatMessage from './ChatMessage';
import RecommendedItemsTray from './RecommendedItems';
import styles from '../berryChat.module.scss';

interface ChatPanelProps {
  messages: IChatMessage[];
  isLoading: boolean;
  isSessionLoading: boolean;
  sessionReady: boolean;
  onSend: (text: string) => void;
  onClose: () => void;
  // tray
  trayItems: IBerryRecommendedItem[] | null;
  /** Cards from earlier replies, reachable from the tray (B76). */
  earlierItems?: IBerryRecommendedItem[];
  /** Shown after "Done adding" so the cards can be brought back (B56). */
  onTrayReopen?: () => void;
  addedItemIds: Set<number>;
  onTrayAdd: (item: IBerryRecommendedItem, quantity: number) => Promise<void>;
  onConfigureItem: (item: IBerryRecommendedItem) => void;
  onTrayDismiss: () => void;
  // persistent checkout
  runningTotal: number;
  checkoutLocation: string | null;
  onCheckout: () => void;
  checkoutLoading: boolean;
  /** Text to put back in the input after a failed send (B83). */
  inputRestore?: { text: string; nonce: number } | null;
  /** Dim the panel while the customize pop-up is open (B82: the site's
   * modal backdrop sits below the panel's z-index). */
  dimmed?: boolean;
}

const ChatPanel: React.FC<ChatPanelProps> = ({
  messages,
  isLoading,
  isSessionLoading,
  sessionReady,
  onSend,
  onClose,
  trayItems,
  earlierItems,
  onTrayReopen,
  addedItemIds,
  onTrayAdd,
  onConfigureItem,
  onTrayDismiss,
  runningTotal,
  checkoutLocation,
  onCheckout,
  checkoutLoading,
  inputRestore,
  dimmed,
}) => {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesRef = useRef<HTMLDivElement>(null);
  const viewport = useVisibleViewport();

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isLoading]);

  // When the visible area shrinks (keyboard opening, Safari's toolbar coming
  // back), keep the newest message in view — scrolling only the message
  // list, never the page (UAT row 15).
  useEffect(() => {
    const list = messagesRef.current;
    if (list) list.scrollTop = list.scrollHeight;
  }, [viewport?.height]);

  // On phones the panel is placed over exactly what's visible (see .panel in
  // berryChat.module.scss); desktop ignores these.
  const viewportStyle = viewport
    ? ({
        '--berry-visible-height': `${Math.round(viewport.height)}px`,
        '--berry-visible-top': `${Math.round(viewport.offsetTop)}px`,
      } as React.CSSProperties)
    : undefined;

  const inputDisabled = isLoading || isSessionLoading || messages.length === 0;

  return (
    <div
      className={styles.panel}
      role="dialog"
      aria-label="Berry catering chat"
      style={viewportStyle}
      data-testid="berry-panel"
    >
      <div className={styles.panelHeader}>
        <div className={styles.headerInfo}>
          <span className={styles.headerEmoji} aria-hidden="true">
            🍓
          </span>
          <div>
            <span className={styles.headerName}>Berry</span>
            <span className={styles.headerSubtitle}>Catering Assistant</span>
          </div>
        </div>
        <button
          className={styles.closeBtn}
          onClick={onClose}
          aria-label="Close Berry chat"
        >
          ✕
        </button>
      </div>

      <div className={styles.messages} role="log" aria-live="polite" ref={messagesRef}>
        {isSessionLoading && messages.length === 0 && (
          <div className={styles.typingWrapper}>
            <div className={styles.typingIndicator} aria-label="Berry is typing">
              <span />
              <span />
              <span />
            </div>
          </div>
        )}

        {messages.map((msg) => (
          <ChatMessage key={msg.id} message={msg} />
        ))}

        {isLoading && (
          <div className={styles.typingWrapper}>
            <div className={styles.typingIndicator} aria-label="Berry is typing">
              <span />
              <span />
              <span />
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {runningTotal > 0 && (
        <CartCompleteCard
          total={runningTotal}
          location={checkoutLocation}
          onCheckout={onCheckout}
          checkoutLoading={checkoutLoading}
        />
      )}

      {trayItems == null && onTrayReopen && (
        <button type="button" className={styles.trayReopenBtn} onClick={onTrayReopen}>
          Show suggestions
        </button>
      )}

      {trayItems != null && (
        <RecommendedItemsTray
          items={trayItems}
          earlierItems={earlierItems}
          addedItemIds={addedItemIds}
          sessionReady={sessionReady}
          onAddToCart={onTrayAdd}
          onConfigureItem={onConfigureItem}
          onDismiss={onTrayDismiss}
        />
      )}

      <ChatInput onSend={onSend} disabled={inputDisabled} restore={inputRestore} />
      {dimmed && <div className={styles.panelDimmer} data-testid="berry-panel-dimmer" />}
    </div>
  );
};

export default ChatPanel;
